import { useAuthStore } from '@/lib/stores/authStore';

/**
 * Ayurveda's case-sheet section order/grouping is fixed in code rather than
 * fetched from the backend departmentConfig. History, Diagnosis, and
 * Treatment (with Diet/Lifestyle nested rows) are hardcoded sections that
 * always render right after this order in CaseSheetView.tsx, and Viruddha
 * Ahaara/Dosha Profile fall back to a fixed trailing position when absent
 * from fieldOrder — together they produce: Chief Complaint, General
 * Examination, Astha Sthaana Pareeksha, Advice & Follow Up, History,
 * Diagnosis, Treatment, Viruddha Ahaara.
 */
const AYURVEDA_FIELD_ORDER = [
  'chiefComplaint',
  'generalExamination',
  'doshaProfile',
  'adviceFollowUp',
];
const AYURVEDA_FIELD_GROUPS: Record<string, string[]> = {
  adviceFollowUp: ['followUpPlan'],
};

/**
 * Hook to access department-specific configuration for case-sheet rendering.
 * For every department except Ayurveda, the backend is the source of truth
 * and always provides a valid departmentConfig, including the built-in
 * default when the DB has no row for the project. Ayurveda's order is
 * hardcoded here instead, so it renders the same fixed case sheet regardless
 * of what (if anything) is stored in the DB for it.
 *
 * Returns:
 * - departmentKey: The department identifier (e.g., "cardiology", "pediatrics")
 * - departmentConfig: The full department configuration object
 * - effectiveFieldOrder: The field order to render (backend config, or hardcoded for Ayurveda)
 * - fieldGroups: Maps UI sections to arrays of database fields (for grouped sections)
 * - shouldShowadtOrder: Whether to show the ADT Orders section
 */
export function useDepartmentConfig() {
  const departmentKey = useAuthStore((state) => state.departmentKey);
  const rawDepartmentConfig = useAuthStore((state) => state.departmentConfig);

  const isAyurveda = departmentKey === 'ayurveda';
  const effectiveFieldOrder = isAyurveda
    ? AYURVEDA_FIELD_ORDER
    : (rawDepartmentConfig?.fieldOrder ?? []);
  const fieldGroups = isAyurveda ? AYURVEDA_FIELD_GROUPS : rawDepartmentConfig?.fieldGroups;
  // Also override the raw departmentConfig object itself, so consumers that
  // read departmentConfig.fieldOrder/fieldGroups directly (e.g. the saved
  // "patient record" view) stay in sync with the hardcoded Ayurveda order too.
  // Patient Summary is deliberately NOT forced in here — Case Sheet (which
  // reads effectiveFieldOrder/departmentConfig straight from this hook) must
  // not show it; Patient Record forces it in separately, on its own, in
  // PatientRecordView.tsx's buildSummarySections.
  const departmentConfig =
    isAyurveda && rawDepartmentConfig
      ? { ...rawDepartmentConfig, fieldOrder: effectiveFieldOrder, fieldGroups }
      : rawDepartmentConfig;
  // ADT Order is shown if 'adtOrder' is present in fieldOrder
  const shouldShowadtOrder = effectiveFieldOrder.includes('adtOrder');

  return {
    departmentKey,
    departmentConfig,
    effectiveFieldOrder,
    fieldGroups,
    shouldShowadtOrder,
  };
}
